import React, { type FC, type ReactNode } from 'react';
import { cssMap } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import ChangesIcon from '@atlaskit/icon/core/changes';
import { Text, Stack, Inline, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	wrapper: {
		width: '360px',
		backgroundColor: token('elevation.surface.overlay'),
		boxShadow: token('elevation.shadow.overlay'),
	},
	changeBtn: {
		width: '100%',
		backgroundColor: token('color.background.neutral.subtle'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		padding: token('space.200'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		textAlign: 'left',
	},
});

const i18n = defineMessages({
	changeStatus: {
		id: 'content-state.popup-base.change-status',
		defaultMessage: 'Change status',
		description: 'Button label that opens the status selection menu',
	},
});

type ContentStatePopupBaseProps = {
	children: ReactNode;
	footer?: ReactNode;
};

/**
 * Shared visual shell for content-state popups (ContentStateInfoPopup, OtherModeReviewBlockingPopup).
 * Renders the 360px overlay wrapper and accepts body content as children.
 * An optional `footer` slot is rendered below the children; if omitted, no footer is shown.
 */
export const ContentStatePopupBase: FC<ContentStatePopupBaseProps> = ({ children, footer }) => {
	return (
		<Stack xcss={styles.wrapper} space="space.050">
			{children}
			{footer}
		</Stack>
	);
};

type PopupFooterButtonProps = {
	onClick: (() => void) | undefined;
	children: ReactNode;
};

/**
 * Reusable footer button with the shared changeBtn styling (full-width, border-top, subtle bg).
 * Used for both "Change status" and "Cancel approval" footer actions.
 */
export const PopupFooterButton: FC<PopupFooterButtonProps> = ({ onClick, children }) => {
	return (
		<Pressable xcss={styles.changeBtn} onClick={onClick}>
			{children}
		</Pressable>
	);
};

type ChangeStatusButtonProps = {
	onClick: (() => void) | undefined;
	label?: string;
};

/**
 * Reusable "Change status" footer button with ChangesIcon.
 */
export const ChangeStatusButton: FC<ChangeStatusButtonProps> = ({ onClick, label }) => {
	const { formatMessage } = useIntl();
	const buttonLabel = label ?? formatMessage(i18n.changeStatus);
	return (
		<PopupFooterButton onClick={onClick}>
			<Text weight="medium">
				<Inline space="space.100" alignBlock="center">
					<ChangesIcon label={buttonLabel} />
					{buttonLabel}
				</Inline>
			</Text>
		</PopupFooterButton>
	);
};
